// Now let's create the Toast component using Headless UI
// src/components/Toast/Toast.tsx
import React, { Fragment, useEffect } from "react";
import { Transition } from "@headlessui/react";
import {
  FaCircleCheck,
  FaCircleExclamation,
  FaCircleInfo,
  FaCircleXmark,
} from "react-icons/fa6";
import { useDispatch } from "react-redux";
import { Toast as ToastType, hideToast } from "../../Redux/Slices/ToastSlice";

interface ToastProps {
  toast: ToastType;
}

const Toast: React.FC<ToastProps> = ({ toast }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(hideToast(toast.id));
    }, toast.duration);

    return () => clearTimeout(timer);
  }, [dispatch, toast.id, toast.duration]);

  const handleClose = () => {
    dispatch(hideToast(toast.id));
  };

  const getIcon = () => {
    switch (toast.type) {
      case "success":
        return (
          <FaCircleCheck
            className="h-6 w-6 text-green-400"
            aria-hidden="true"
          />
        );
      case "error":
        return (
          <FaCircleExclamation
            className="h-6 w-6 text-red-400"
            aria-hidden="true"
          />
        );
      case "warning":
        return (
          <FaCircleExclamation
            className="h-6 w-6 text-yellow-400"
            aria-hidden="true"
          />
        );
      case "info":
      default:
        return (
          <FaCircleInfo className="h-6 w-6 text-blue-400" aria-hidden="true" />
        );
    }
  };

  const getToastClasses = () => {
    const baseClasses =
      "max-w-sm w-full shadow-xl rounded-lg pointer-events-auto ring-1 overflow-hidden z-[99999]";

    switch (toast.type) {
      case "success":
        return `${baseClasses} ring-green-500 bg-green-50 dark:bg-green-900/30`;
      case "error":
        return `${baseClasses} ring-red-500 bg-red-50 dark:bg-red-900/30`;
      case "warning":
        return `${baseClasses} ring-yellow-500 bg-yellow-50 dark:bg-yellow-900/30`;
      case "info":
      default:
        return `${baseClasses} ring-blue-500 bg-blue-50 dark:bg-blue-900/30`;
    }
  };

  return (
    <Transition
      show={true}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className={getToastClasses()}>
        <div className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">{getIcon()}</div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {toast.message}
              </p>
            </div>
            <div className="ml-4 flex-shrink-0 flex">
              <button
                className="bg-white dark:bg-transparent rounded-md inline-flex text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <FaCircleXmark className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default Toast;
